import _orderBy from 'lodash/orderBy';
import _keyBy from 'lodash/keyBy';
import _uniqBy from 'lodash/uniqBy';
import _sortBy from 'lodash/sortBy';
import { shallowEqual } from 'react-redux';

function getOrderedIds({
  entitiesById = {},
  idKey = 'id',
  orderKey,
  sortOrder = 'asc',
}) {
  const orderedEntities = _orderBy(entitiesById, [orderKey], sortOrder);
  return orderedEntities.map((entity) => {
    return entity[idKey];
  });
}

function getOrderedActivitySetRefs(activitySetsByRef) {
  return getOrderedIds({
    entitiesById: activitySetsByRef,
    idKey: 'activitySetRef',
    orderKey: 'closedAt',
    sortOrder: 'desc',
  });
}

function getOrderedActivityRefs(activitiesByRef) {
  return getOrderedIds({
    entitiesById: activitiesByRef,
    idKey: 'activityRef',
    orderKey: 'start',
    sortOrder: 'desc',
  });
}

function getOrderedJobIds(jobsById) {
  return getOrderedIds({
    entitiesById: jobsById,
    idKey: 'jobId',
    // match default order of operator-view/jobs query
    orderKey: 'name',
  });
}

function getNormalizedSetPollJobData({ setupTime, ...job }) {
  return {
    ...job,
    // endpoint sends as ms, but this is seconds in graphql schema and elsehwere in store
    // be sure to preserve null if present (represents job without expected setup time)
    setupTime: typeof setupTime === 'number' ? setupTime / 1000 : null,
  };
}

function getNormalizedActivitySetData(recentActivitySets = []) {
  const {
    activitySetsByRef,
    activitiesByRef,
    jobsById,
  } = recentActivitySets.reduce(
    (normalizedEntities, { job, activities, ...activitySet }) => {
      const { activitySetRef } = activitySet;
      return {
        activitySetsByRef: {
          ...normalizedEntities.activitySetsByRef,
          [activitySetRef]: activitySet,
        },
        activitiesByRef: {
          ...normalizedEntities.activitiesByRef,
          ..._keyBy(activities, 'activityRef'),
        },
        jobsById: {
          ...normalizedEntities.jobsById,
          ...(job?.jobId
            ? { [job.jobId]: getNormalizedSetPollJobData(job) }
            : {}),
        },
      };
    },
    { activitySetsbyRef: {}, activitiesbyRef: {}, jobsById: {} }
  );
  // intentionally overwriting
  return {
    activitySets: {
      byRef: activitySetsByRef,
      allRefs: getOrderedActivitySetRefs(activitySetsByRef),
    },
    activities: {
      byRef: activitiesByRef,
      allRefs: getOrderedActivityRefs(activitiesByRef),
    },
    jobs: {
      byId: jobsById,
      allIds: getOrderedJobIds(jobsById),
    },
  };
}

function setRecentActivitySetsData(state, activitySets) {
  const normalizedData = getNormalizedActivitySetData(activitySets);
  // note that we overwrite everything here on new poll data and don't merge it; this will need to change
  // when multiple sets on opdash are supported at a time, or if other components use job data from store
  return {
    ...state,
    recentActivitySetsPoll: {
      isInitialPollLoading: false,
      hasWriteLock: false,
    },
    activitySets: {
      byRef: normalizedData.activitySets.byRef,
      allRefs: normalizedData.activitySets.allRefs,
    },
    activities: {
      byRef: normalizedData.activities.byRef,
      allRefs: normalizedData.activities.allRefs,
    },
    jobs: {
      ...state.jobs,
      byId: normalizedData.jobs.byId,
      // there will only ever be one job at a time in store (for now), so this is ok
      allIds: Object.keys(normalizedData.jobs.byId),
    },
  };
}

function getSortedUniqueAnnotations(state, actionAnnotations) {
  const mergedAnnos = [...state.annotations.data, ...actionAnnotations];
  const uniqueAnnos = _uniqBy(mergedAnnos, (anno) => {
    return anno.id;
  });
  const sortedAnnos = _sortBy(uniqueAnnos, (anno) => {
    return anno.start;
  });
  return sortedAnnos;
}

function manualPartCountCleanup(manualPartBuckets, userAddedManualPartCounts) {
  // Find latest database sync time
  const mostRecentLastBucketEventTime = manualPartBuckets.reduce(
    (mostRecent, curr) => {
      if (!mostRecent) return curr.lastBucketEventTime;
      return new Date(curr.lastBucketEventTime) > new Date(mostRecent)
        ? curr.lastBucketEventTime
        : mostRecent;
    },
    null
  );

  if (!mostRecentLastBucketEventTime) {
    return userAddedManualPartCounts;
  }

  // Keep only parts added after database sync
  const remainingPartCounts = userAddedManualPartCounts.filter(
    ({ partTime }) => {
      if (!mostRecentLastBucketEventTime) return true;
      return new Date(partTime) > new Date(mostRecentLastBucketEventTime);
    }
  );

  return remainingPartCounts;
}

// Long enough to cover the poll or two the Dashboard misses after a submit, short enough that a
// Dashboard that keeps failing shows as this machine's own count rather than a frozen total.
const CARRIED_OPERATION_TOTALS_MAX_AGE_MS = 45 * 1000;

const isSameEvent = (event, previous) => {
  const keys = Object.keys(event);
  return (
    previous != null &&
    keys.length === Object.keys(previous).length &&
    keys.every((key) => {
      return key === 'orderQuantity'
        ? shallowEqual(event[key], previous[key])
        : event[key] === previous[key];
    })
  );
};

/**
 * The feed leaves a card's operation totals out when the Dashboard can't work them out in time,
 * which is common for a poll or two after any machine on the operation submits. Keeps each
 * operation's last totals (carried: by operation ref, with when the feed last sent them) and fills
 * them into events that come without them for up to CARRIED_OPERATION_TOTALS_MAX_AGE_MS, never
 * below this machine's own good parts, which keep counting while a carried total doesn't.
 * @param {Object} carried - The kept totals so far
 * @param {Array} events - This poll's events
 * @param {number} now - Epoch ms
 * @param {Array} [previousEvents] - The events stored last poll, kept when filling gives the same
 * @returns {{carried: Object, events: Array}} The kept totals, less any expired, and the events
 */
function carryForwardOperationTotals(
  carried = {},
  events = [],
  now,
  previousEvents = []
) {
  const fresh = Object.fromEntries(
    Object.entries(carried).filter(([, totals]) => {
      return now - totals.lastSeenAt <= CARRIED_OPERATION_TOTALS_MAX_AGE_MS;
    })
  );
  events.forEach((event) => {
    const { workOrderOperationRef, orderQuantity } = event;
    if (
      workOrderOperationRef != null &&
      orderQuantity?.operationGoodQuantity != null
    ) {
      fresh[String(workOrderOperationRef)] = {
        operationGoodQuantity: orderQuantity.operationGoodQuantity,
        operationMachineCount: orderQuantity.operationMachineCount,
        lastSeenAt: now,
      };
    }
  });
  const needsFill = (event) => {
    return (
      event.orderQuantity != null &&
      event.orderQuantity.operationGoodQuantity == null &&
      fresh[String(event.workOrderOperationRef)] != null
    );
  };
  const fill = (event) => {
    if (!needsFill(event)) return event;
    const last = fresh[String(event.workOrderOperationRef)];
    return {
      ...event,
      orderQuantity: {
        ...event.orderQuantity,
        operationGoodQuantity: Math.max(
          last.operationGoodQuantity,
          event.orderQuantity.goodQuantity ?? 0
        ),
        operationMachineCount: last.operationMachineCount,
      },
    };
  };
  // The same array when nothing needs filling, or filling gives last poll's events again (events are
  // stored after every poll), so selectors and the embedded frames on programEvents keep their results.
  if (!events.some(needsFill)) {
    return { carried: fresh, events };
  }
  const filled = events.map(fill);
  const unchanged =
    filled.length === previousEvents.length &&
    filled.every((event, index) => {
      return isSameEvent(event, previousEvents[index]);
    });
  return { carried: fresh, events: unchanged ? previousEvents : filled };
}

export {
  carryForwardOperationTotals,
  setRecentActivitySetsData,
  getOrderedActivitySetRefs,
  getOrderedActivityRefs,
  getOrderedJobIds,
  getSortedUniqueAnnotations,
  manualPartCountCleanup,
};
